import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, ClipboardList, Wrench, Truck, ChevronDown, ChevronRight, Clock, User, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

// === Read-only VO checklist items (for admin consultation in parc dialog) ===
const VO_CHECKLIST_ITEMS = [
  { key: 'nettoyage_exterieur', label: 'Nettoyage exterieur' },
  { key: 'nettoyage_interieur', label: 'Nettoyage interieur' },
  { key: 'etat_carrosserie', label: 'Etat de la carrosserie' },
  { key: 'etat_parechocs', label: 'Etat des pare-chocs' },
  { key: 'etat_jantes', label: 'Etat des jantes' },
  { key: 'etat_pneus', label: 'Etat des pneus' },
  { key: 'etat_interieur', label: "Etat de l'interieur" },
];

// ======================================
// PARC OCCASION DIALOG (new workflow)
// ======================================
const ParcOccasionDialog = ({ vehicleId, vehicleName, open, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [woRecap, setWoRecap] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  // Creation form
  const [transmittedDate, setTransmittedDate] = useState(new Date().toISOString().slice(0, 10));
  const [transmittedBy, setTransmittedBy] = useState('');

  const loadPrep = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations`);
      const parcPrep = res.data?.parc;
      if (parcPrep) {
        setPrep(parcPrep);
        // Load WO recap
        if (parcPrep.work_order_id) {
          try {
            const recapRes = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations/${parcPrep.id}/wo-recap`);
            setWoRecap(recapRes.data);
            if (recapRes.data.atelier_ready) {
              setShowRecap(true);
            }
          } catch { /* ignore */ }
        }
      } else {
        setPrep(null);
        setWoRecap(null);
      }
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { if (open) { loadPrep(); setShowChecklist(false); setShowRecap(false); } }, [open, loadPrep]);

  const handleCreate = async () => {
    if (!transmittedBy.trim()) { toast.error('Veuillez indiquer qui transmet'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/used-vehicles/${vehicleId}/preparations/parc`, {
        transmitted_date: transmittedDate,
        transmitted_by: transmittedBy.trim(),
      });
      setPrep(res.data);
      toast.success('Fiche creee et fiche de travail generee');
      if (res.data.work_order_id) {
        try {
          const recapRes = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations/${res.data.id}/wo-recap`);
          setWoRecap(recapRes.data);
        } catch { /* ignore */ }
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!prep || !woRecap) return;
    setSaving(true);
    try {
      await axios.post(`${API}/used-vehicles/${vehicleId}/preparations/${prep.id}/finalize`, {
        total_cost: woRecap.total_cost,
      });
      setPrep(p => ({ ...p, finalized: true }));
      toast.success(`Frais de remise en etat (CHF ${woRecap.total_cost.toFixed(2)}) ajoutes au prix de revient`);
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!prep || !window.confirm('Supprimer cette fiche de preparation ?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/used-vehicles/${vehicleId}/preparations/${prep.id}`);
      setPrep(null);
      setWoRecap(null);
      toast.success('Fiche supprimee');
      if (onUpdate) onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Compute atelier checklist stats from woRecap
  const checklistData = woRecap?.preparation_checklist?.checklist || {};
  const checklistNotes = woRecap?.preparation_checklist?.notes || {};
  const totalCheckItems = VO_CHECKLIST_ITEMS.length;
  const checkedItems = VO_CHECKLIST_ITEMS.filter(item => checklistData[item.key]).length;

  // Determine phase
  const isCreated = !!prep;
  const hasWo = !!prep?.work_order_id;
  const isAtelierReady = woRecap?.atelier_ready === true;
  const isFinalized = prep?.finalized === true;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="prep-dialog-parc" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-700" />
            <span>Fiche preparation parc occasion</span>
          </DialogTitle>
          {vehicleName && <p className="text-sm text-slate-500 mt-1">{vehicleName}</p>}
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Chargement...</div>
        ) : !isCreated ? (
          /* ========== PHASE 1: Creation form ========== */
          <div className="space-y-4 py-2" data-testid="prep-creation-form">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <p className="text-sm font-semibold text-blue-800">Transmettre le vehicule a l'atelier pour controle</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" /> Date de transmission
                  </label>
                  <Input type="date" value={transmittedDate} onChange={e => setTransmittedDate(e.target.value)}
                    className="h-9 text-sm" data-testid="prep-transmitted-date" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    <User className="w-3 h-3" /> Transmis par
                  </label>
                  <Input value={transmittedBy} onChange={e => setTransmittedBy(e.target.value)}
                    placeholder="Nom de la personne" className="h-9 text-sm" data-testid="prep-transmitted-by" />
                </div>
              </div>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="prep-create-btn">
              <ClipboardList className="w-4 h-4 mr-2" />
              {saving ? 'Creation en cours...' : 'Creer la fiche parc occasion'}
            </Button>
          </div>
        ) : (
          /* ========== PHASES 2-4: Prep exists ========== */
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {/* Transmission info */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                    <span>Transmis le <strong>{prep.transmitted_date || prep.created_at?.slice(0, 10)}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span>par <strong>{prep.transmitted_by || '-'}</strong></span>
                  </div>
                </div>
                {/* Status badge */}
                {isFinalized ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="prep-status-badge">Finalise</span>
                ) : isAtelierReady ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid="prep-status-badge">Pret - A chiffrer</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium animate-pulse" data-testid="prep-status-badge">En cours a l'atelier</span>
                )}
              </div>
            </div>

            {/* Atelier ready info */}
            {isAtelierReady && !isFinalized && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="atelier-ready-info">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Vehicule pret - En attente de chiffrage</p>
                    <p className="text-xs text-amber-600">
                      Marque pret par {woRecap?.atelier_ready_by || 'Atelier'}
                      {woRecap?.atelier_ready_at && ` le ${new Date(woRecap.atelier_ready_at).toLocaleDateString('fr-CH')}`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Not ready yet */}
            {!isAtelierReady && !isFinalized && hasWo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="atelier-in-progress">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600 animate-pulse" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">En cours de controle par l'atelier</p>
                    <p className="text-xs text-blue-600">
                      Progression checklist : {checkedItems}/{totalCheckItems}
                      {totalCheckItems > 0 && ` (${Math.round((checkedItems / totalCheckItems) * 100)}%)`}
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${totalCheckItems > 0 ? Math.round((checkedItems / totalCheckItems) * 100) : 0}%` }} />
                </div>
              </div>
            )}

            {/* Atelier checklist (read-only, consultable) */}
            {hasWo && checkedItems > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="atelier-checklist-section">
                <button className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
                  onClick={() => setShowChecklist(!showChecklist)}>
                  <div className="flex items-center gap-2">
                    {showChecklist ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-700">Controles atelier</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-mono">{checkedItems}/{totalCheckItems}</span>
                  </div>
                </button>
                {showChecklist && (
                  <div className="px-3 py-2 space-y-1.5">
                    {VO_CHECKLIST_ITEMS.map(item => {
                      const value = checklistData[item.key] || '';
                      const note = checklistNotes[item.key] || '';
                      if (!value) return null;
                      const isGood = value === 'Bon';
                      const isBad = value === 'Pas bon';
                      return (
                        <div key={item.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isGood ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : isBad ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isGood ? 'bg-emerald-500' : isBad ? 'bg-red-500' : 'bg-amber-500'}`} />
                          <span className="font-medium flex-1">{item.label}</span>
                          <span className="text-xs font-semibold">{value}</span>
                          {note && <span className="text-[10px] italic opacity-75 ml-1">({note})</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* WO Recap / Chiffrage section */}
            {woRecap && woRecap.work_order_id && (isAtelierReady || isFinalized) && (
              <div className="space-y-3" data-testid="wo-recap-section">
                <button className="w-full flex items-center justify-between" onClick={() => setShowRecap(!showRecap)}>
                  <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-amber-600" /> Chiffrage
                  </h4>
                  {showRecap ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>

                {showRecap && (
                  <div className="space-y-3">
                    {/* Parts */}
                    {woRecap.parts.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <h5 className="text-xs font-bold text-slate-700 uppercase mb-2">Pieces utilisees</h5>
                        <div className="space-y-1">
                          {woRecap.parts.map(p => (
                            <div key={p.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-700">{p.name}</span>
                                <span className="text-xs text-slate-400">x{p.quantity}</span>
                                {(p.sell_price || 0) <= 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Prix a confirmer</span>}
                              </div>
                              <span className="font-mono text-sm font-medium">
                                {(p.sell_price || 0) > 0 ? `CHF ${fmt(p.sell_price * (p.quantity || 1))}` : '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Labor */}
                    {woRecap.labor_minutes > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <h5 className="text-xs font-bold text-slate-700 uppercase mb-2">Main d'oeuvre</h5>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">{Math.round(woRecap.labor_minutes)} min ({(woRecap.labor_minutes / 60).toFixed(1)}h) @ CHF {fmt(woRecap.labor_rate)}/h</span>
                          <span className="font-mono font-medium">CHF {fmt(woRecap.total_labor)}</span>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-amber-900">Frais de remise en etat</span>
                        <span className="text-lg font-bold text-amber-800">CHF {fmt(woRecap.total_cost)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-amber-600 mt-1">
                        <span>Pieces: CHF {fmt(woRecap.total_parts)}</span>
                        <span>Main d'oeuvre: CHF {fmt(woRecap.total_labor)}</span>
                      </div>
                    </div>

                    {/* Finalize button */}
                    {!isFinalized && (
                      <Button onClick={handleFinalize} disabled={saving}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="finalize-prep-btn">
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Valider et ajouter au prix de revient
                      </Button>
                    )}
                    {isFinalized && (
                      <div className="text-center text-sm text-emerald-600 font-medium py-2" data-testid="finalized-message">
                        <CheckCircle2 className="w-4 h-4 inline mr-1" /> Frais de remise en etat valides et ajoutes au prix de revient
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Finalized success */}
            {isFinalized && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center" data-testid="prep-finalized-banner">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                <p className="text-sm font-semibold text-emerald-700">Preparation finalisee</p>
                <p className="text-xs text-emerald-600">Les frais de remise en etat ont ete integres au prix de revient</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
              {hasWo && (
                <Button variant="outline" className="text-sm text-blue-600" onClick={() => { onClose(false); navigate(`/admin/work-orders/${prep.work_order_id}`); }}
                  data-testid="prep-view-wo-btn">
                  <Wrench className="w-4 h-4 mr-1" /> Voir la fiche de travail
                </Button>
              )}
              {!isFinalized && (
                <Button variant="outline" className="text-sm text-red-500 hover:bg-red-50 ml-auto" onClick={handleDelete}
                  disabled={saving} data-testid="prep-delete-btn">
                  Supprimer
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ======================================
// LIVRAISON DIALOG (new workflow - similar to ParcOccasion)
// ======================================
const LivraisonDialog = ({ vehicleId, vehicleName, open, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [woRecap, setWoRecap] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  // Creation form
  const [exOwnerName, setExOwnerName] = useState('');
  const [newClientSearch, setNewClientSearch] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [clients, setClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  // Checklist items for "Demandes"
  const [checklistItems, setChecklistItems] = useState({});
  // Livraison prep checklist (pre-fill for atelier)
  const [livraisonChecklist, setLivraisonChecklist] = useState({});

  const loadPrep = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations`);
      const livraisonPrep = res.data?.livraison;
      if (livraisonPrep) {
        setPrep(livraisonPrep);
        if (livraisonPrep.work_order_id) {
          try {
            const recapRes = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations/${livraisonPrep.id}/wo-recap`);
            setWoRecap(recapRes.data);
          } catch { /* ignore */ }
        }
      } else {
        setPrep(null);
        setWoRecap(null);
        // Load vehicle info for ex-owner default
        try {
          const vRes = await axios.get(`${API}/used-vehicles/${vehicleId}`);
          setExOwnerName(vRes.data.owner_name || '');
        } catch { /* ignore */ }
      }
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [vehicleId]);

  useEffect(() => { if (open) { loadPrep(); setShowChecklist(false); } }, [open, loadPrep]);

  // Client search
  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClients([]); return; }
    try {
      const res = await axios.get(`${API}/clients?search=${encodeURIComponent(q)}`);
      setClients(res.data.slice(0, 8));
      setShowClientDropdown(true);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (newClientSearch.length >= 2) searchClients(newClientSearch); }, 300);
    return () => clearTimeout(t);
  }, [newClientSearch, searchClients]);

  const selectClient = (c) => {
    setNewClientId(c.id);
    setNewClientName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    setNewClientSearch(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    setShowClientDropdown(false);
  };

  const handleCreate = async () => {
    if (!newClientName.trim() && !newClientSearch.trim()) { toast.error('Veuillez selectionner le nouveau client'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/used-vehicles/${vehicleId}/preparations/livraison`, {
        transmitted_date: new Date().toISOString().slice(0, 10),
        transmitted_by: '',
        ex_owner_name: exOwnerName,
        new_client_id: newClientId,
        new_client_name: newClientName || newClientSearch.trim(),
        checklist_items: checklistItems,
        livraison_checklist: livraisonChecklist,
      });
      setPrep(res.data);
      toast.success('Fiche livraison creee et fiche de travail generee');
      if (res.data.work_order_id) {
        try {
          const recapRes = await axios.get(`${API}/used-vehicles/${vehicleId}/preparations/${res.data.id}/wo-recap`);
          setWoRecap(recapRes.data);
        } catch { /* ignore */ }
      }
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleFinalize = async () => {
    if (!prep) return;
    setSaving(true);
    try {
      await axios.post(`${API}/used-vehicles/${vehicleId}/preparations/${prep.id}/finalize-livraison`);
      setPrep(p => ({ ...p, finalized: true }));
      toast.success('Vehicule pret a etre livre !');
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!prep || !window.confirm('Supprimer cette fiche de livraison ?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/used-vehicles/${vehicleId}/preparations/${prep.id}`);
      setPrep(null);
      setWoRecap(null);
      toast.success('Fiche supprimee');
      if (onUpdate) onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // Read-only checklist from WO
  const LIVRAISON_ITEMS = [
    { key: 'nettoyage_exterieur', label: 'Nettoyage exterieur' },
    { key: 'nettoyage_interieur', label: 'Nettoyage interieur' },
    { key: 'controle_global_interieur', label: 'Controle global interieur' },
    { key: 'vitres', label: 'Vitres' },
    { key: 'horloge_vehicule', label: 'Horloge vehicule' },
    { key: 'temoins_tableau_bord', label: 'Temoins tableau de bord' },
    { key: 'roues_ete', label: 'Roues ete' },
    { key: 'roues_hiver', label: 'Roues hiver' },
  ];
  const checklistData = woRecap?.preparation_checklist?.checklist || {};
  const checklistNotes = woRecap?.preparation_checklist?.notes || {};
  const checkedItems = LIVRAISON_ITEMS.filter(item => checklistData[item.key]).length;
  const totalCheckItems = LIVRAISON_ITEMS.length;

  const isCreated = !!prep;
  const hasWo = !!prep?.work_order_id;
  const isAtelierReady = woRecap?.atelier_ready === true;
  const isFinalized = prep?.finalized === true;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="prep-dialog-livraison" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-700" />
            <span>Preparation pour livraison</span>
          </DialogTitle>
          {vehicleName && <p className="text-sm text-slate-500 mt-1">{vehicleName}</p>}
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Chargement...</div>
        ) : !isCreated ? (
          /* ========== Creation form ========== */
          <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1" data-testid="livraison-creation-form">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-4">
              <p className="text-sm font-semibold text-emerald-800">Creer la fiche de preparation livraison</p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    <User className="w-3 h-3" /> Ex-proprietaire
                  </label>
                  <Input value={exOwnerName} onChange={e => setExOwnerName(e.target.value)}
                    placeholder="Nom de l'ex-proprietaire" className="h-9 text-sm" data-testid="livraison-ex-owner"
                    readOnly={!!exOwnerName} />
                  {exOwnerName && <p className="text-[10px] text-slate-400">Rempli automatiquement depuis la carte grise</p>}
                </div>
                <div className="space-y-1.5 relative">
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    <User className="w-3 h-3" /> Nouveau client
                  </label>
                  <Input value={newClientSearch} onChange={e => { setNewClientSearch(e.target.value); setNewClientName(''); setNewClientId(''); }}
                    placeholder="Rechercher un client..." className="h-9 text-sm" data-testid="livraison-new-client"
                    onFocus={() => { if (clients.length) setShowClientDropdown(true); }} />
                  {showClientDropdown && clients.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1">
                      {clients.map(c => (
                        <button key={c.id} onClick={() => selectClient(c)}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm flex items-center justify-between" data-testid={`client-option-${c.id}`}>
                          <span className="font-medium">{c.first_name} {c.last_name}</span>
                          {c.phone && <span className="text-xs text-slate-400">{c.phone}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {newClientName && <p className="text-xs text-emerald-600 font-medium">Client selectionne : {newClientName}</p>}
                </div>
              </div>
            </div>
            {/* Demandes du client / travaux a effectuer */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="livraison-demandes">
              <h4 className="text-sm font-bold text-slate-900">Demandes / Travaux a effectuer</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Mecanique</p>
                  {[
                    { key: 'service_standard', label: 'Service standard' },
                    { key: 'grand_service', label: 'Grand service' },
                    { key: 'diagnostic', label: 'Diagnostic' },
                    { key: 'changement_roues', label: 'Changement de roues' },
                    { key: 'montage_pneus', label: 'Montage pneus' },
                    { key: 'controle_expertise', label: 'Controle avant expertise' },
                    { key: 'preparation_expertise', label: 'Preparation expertise' },
                    { key: 'presentation_expertise', label: 'Presentation expertise' },
                  ].map(item => (
                    <label key={item.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`demande-${item.key}`}>
                      <Checkbox checked={!!checklistItems[item.key]} onCheckedChange={() => setChecklistItems(prev => ({ ...prev, [item.key]: !prev[item.key] }))} />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Carrosserie</p>
                  {[
                    { key: 'tolerie', label: 'Tolerie' },
                    { key: 'peinture', label: 'Peinture' },
                    { key: 'remplacement_pare_brise', label: 'Remplacement pare-brise' },
                    { key: 'infiltration_eau', label: "Infiltration d'eau" },
                  ].map(item => (
                    <label key={item.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`demande-${item.key}`}>
                      <Checkbox checked={!!checklistItems[item.key]} onCheckedChange={() => setChecklistItems(prev => ({ ...prev, [item.key]: !prev[item.key] }))} />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assurance</p>
                  <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="demande-cas_assurance">
                    <Checkbox checked={!!checklistItems['cas_assurance']} onCheckedChange={() => setChecklistItems(prev => ({ ...prev, cas_assurance: !prev.cas_assurance }))} />
                    <span>Cas assurance</span>
                  </label>
                  {checklistItems['cas_assurance'] && (
                    <div className="space-y-1.5 ml-6">
                      <Input value={checklistItems['sinistre_1'] || ''} onChange={e => setChecklistItems(prev => ({ ...prev, sinistre_1: e.target.value }))}
                        placeholder="N° sinistre 1" className="h-7 text-xs" data-testid="sinistre-1" />
                      <Input value={checklistItems['sinistre_2'] || ''} onChange={e => setChecklistItems(prev => ({ ...prev, sinistre_2: e.target.value }))}
                        placeholder="N° sinistre 2" className="h-7 text-xs" data-testid="sinistre-2" />
                      <Input value={checklistItems['sinistre_3'] || ''} onChange={e => setChecklistItems(prev => ({ ...prev, sinistre_3: e.target.value }))}
                        placeholder="N° sinistre 3" className="h-7 text-xs" data-testid="sinistre-3" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Demande speciale</p>
                  {[
                    { key: 'lavage_moteur', label: 'Lavage moteur' },
                    { key: 'lavage_chassis', label: 'Lavage chassis' },
                  ].map(item => (
                    <label key={item.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`demande-${item.key}`}>
                      <Checkbox checked={!!checklistItems[item.key]} onCheckedChange={() => setChecklistItems(prev => ({ ...prev, [item.key]: !prev[item.key] }))} />
                      <span>{item.label}</span>
                    </label>
                  ))}
                  <Input value={checklistItems['demande_speciale_texte'] || ''} onChange={e => setChecklistItems(prev => ({ ...prev, demande_speciale_texte: e.target.value }))}
                    placeholder="Autre demande..." className="h-7 text-xs" data-testid="demande-speciale-texte" />
                </div>
              </div>
            </div>
            {/* Livraison checklist - travaux preparation */}
            <div className="bg-white border border-orange-200 rounded-lg p-4 space-y-3" data-testid="livraison-prep-checklist">
              <h4 className="text-sm font-bold text-orange-800">Preparation livraison</h4>
              <div className="space-y-2">
                {/* Basic items */}
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-nettoyage_exterieur">
                  <Checkbox checked={livraisonChecklist['nettoyage_exterieur'] === 'A faire'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, nettoyage_exterieur: c ? 'A faire' : undefined }))} />
                  <span>Nettoyage exterieur</span>
                  {livraisonChecklist['nettoyage_exterieur'] && <span className="text-xs text-orange-600 font-medium ml-auto">A faire</span>}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-nettoyage_interieur">
                  <Checkbox checked={livraisonChecklist['nettoyage_interieur'] === 'A faire'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, nettoyage_interieur: c ? 'A faire' : undefined }))} />
                  <span>Nettoyage interieur</span>
                  {livraisonChecklist['nettoyage_interieur'] && <span className="text-xs text-orange-600 font-medium ml-auto">A faire</span>}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-nettoyage_moteur">
                  <Checkbox checked={livraisonChecklist['nettoyage_moteur'] === 'A faire'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, nettoyage_moteur: c ? 'A faire' : undefined }))} />
                  <span>Nettoyage moteur</span>
                  {livraisonChecklist['nettoyage_moteur'] && <span className="text-xs text-orange-600 font-medium ml-auto">A faire</span>}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-vitres">
                  <Checkbox checked={livraisonChecklist['vitres'] === 'A faire'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, vitres: c ? 'A faire' : undefined }))} />
                  <span>Vitres</span>
                  {livraisonChecklist['vitres'] && <span className="text-xs text-orange-600 font-medium ml-auto">A faire</span>}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-horloge_vehicule">
                  <Checkbox checked={livraisonChecklist['horloge_vehicule'] === 'A regler'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, horloge_vehicule: c ? 'A regler' : undefined }))} />
                  <span>Horloge a regler</span>
                  {livraisonChecklist['horloge_vehicule'] && <span className="text-xs text-orange-600 font-medium ml-auto">A regler</span>}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="livr-prep-temoins_tableau_bord">
                  <Checkbox checked={livraisonChecklist['temoins_tableau_bord'] === 'A controler'}
                    onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, temoins_tableau_bord: c ? 'A controler' : undefined }))} />
                  <span>Temoins tableau de bord a controler</span>
                  {livraisonChecklist['temoins_tableau_bord'] && <span className="text-xs text-orange-600 font-medium ml-auto">A controler</span>}
                </label>

                {/* Roues - multiple options */}
                <div className="border border-slate-200 rounded-lg p-2 space-y-1.5">
                  <p className="text-xs font-bold text-slate-600">Roues ete</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Checkbox checked={livraisonChecklist['roues_ete_monter'] === true}
                        onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, roues_ete_monter: c || undefined }))} />
                      <span>A monter</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Checkbox checked={livraisonChecklist['roues_ete_coffre'] === true}
                        onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, roues_ete_coffre: c || undefined }))} />
                      <span>Dans le coffre</span>
                    </label>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-lg p-2 space-y-1.5">
                  <p className="text-xs font-bold text-slate-600">Roues hiver</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Checkbox checked={livraisonChecklist['roues_hiver_monter'] === true}
                        onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, roues_hiver_monter: c || undefined }))} />
                      <span>A monter</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Checkbox checked={livraisonChecklist['roues_hiver_coffre'] === true}
                        onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, roues_hiver_coffre: c || undefined }))} />
                      <span>Dans le coffre</span>
                    </label>
                  </div>
                </div>

                {/* Controle global - opens sub-checklist */}
                <div className="border border-orange-200 rounded-lg overflow-hidden">
                  <label className="flex items-center gap-2 cursor-pointer text-sm p-2 bg-orange-50" data-testid="livr-prep-controle_global">
                    <Checkbox checked={livraisonChecklist['controle_global'] === 'A faire'}
                      onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, controle_global: c ? 'A faire' : undefined }))} />
                    <span className="font-semibold text-orange-800">Controle global</span>
                    {livraisonChecklist['controle_global'] && <span className="text-xs text-orange-600 font-medium ml-auto">A faire</span>}
                  </label>
                  {livraisonChecklist['controle_global'] === 'A faire' && (
                    <div className="p-2 grid grid-cols-2 gap-1.5" data-testid="controle-global-items">
                      {[
                        { key: 'cg_carrosserie', label: 'Etat general carrosserie' },
                        { key: 'cg_pneus', label: 'Etat pneus' },
                        { key: 'cg_parechocs', label: 'Etat pare-chocs' },
                        { key: 'cg_essai_routier', label: 'Essai routier' },
                        { key: 'cg_bruits_moteur', label: 'Bruits moteur' },
                        { key: 'cg_defauts', label: 'Paquet de defauts' },
                        { key: 'cg_eclairage', label: 'Eclairage' },
                        { key: 'cg_freins', label: 'Freins (disques, plaquettes)' },
                        { key: 'cg_direction', label: 'Direction' },
                        { key: 'cg_suspension', label: 'Suspension' },
                        { key: 'cg_echappement', label: 'Echappement' },
                        { key: 'cg_niveaux', label: 'Niveaux (huile, liquides)' },
                        { key: 'cg_climatisation', label: 'Climatisation' },
                        { key: 'cg_vitres_retro', label: 'Vitres / retroviseurs' },
                        { key: 'cg_ceintures', label: 'Ceintures securite' },
                      ].map(item => (
                        <label key={item.key} className="flex items-center gap-1.5 cursor-pointer text-xs px-2 py-1 rounded hover:bg-orange-50" data-testid={`cg-${item.key}`}>
                          <Checkbox className="h-3.5 w-3.5" checked={livraisonChecklist[item.key] === 'A controler'}
                            onCheckedChange={c => setLivraisonChecklist(p => ({ ...p, [item.key]: c ? 'A controler' : undefined }))} />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="livraison-create-btn">
              <Truck className="w-4 h-4 mr-2" />
              {saving ? 'Creation en cours...' : 'Creer la fiche livraison'}
            </Button>
          </div>
        ) : (
          /* ========== Prep exists - workflow phases ========== */
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {/* Info bar */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  {prep.ex_owner_name && (
                    <span className="text-slate-600">Ex : <strong>ex {prep.ex_owner_name}</strong></span>
                  )}
                  <span className="text-slate-600">Client : <strong>{prep.new_client_name || '-'}</strong></span>
                </div>
                {isFinalized ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="livraison-status-badge">Pret a livrer</span>
                ) : isAtelierReady ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid="livraison-status-badge">Pret - A finaliser</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium animate-pulse" data-testid="livraison-status-badge">En cours a l'atelier</span>
                )}
              </div>
            </div>

            {/* Atelier ready */}
            {isAtelierReady && !isFinalized && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Vehicule pret - En attente de finalisation</p>
                    <p className="text-xs text-amber-600">
                      Marque pret par {woRecap?.atelier_ready_by || 'Atelier'}
                      {woRecap?.atelier_ready_at && ` le ${new Date(woRecap.atelier_ready_at).toLocaleDateString('fr-CH')}`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* In progress */}
            {!isAtelierReady && !isFinalized && hasWo && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600 animate-pulse" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">En cours de preparation par l'atelier</p>
                    <p className="text-xs text-emerald-600">Progression : {checkedItems}/{totalCheckItems}</p>
                  </div>
                </div>
                <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${totalCheckItems > 0 ? Math.round((checkedItems / totalCheckItems) * 100) : 0}%` }} />
                </div>
              </div>
            )}

            {/* Read-only checklist from atelier */}
            {hasWo && checkedItems > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="livraison-checklist-section">
                <button className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
                  onClick={() => setShowChecklist(!showChecklist)}>
                  <div className="flex items-center gap-2">
                    {showChecklist ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-700">Controles atelier</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-mono">{checkedItems}/{totalCheckItems}</span>
                  </div>
                </button>
                {showChecklist && (
                  <div className="px-3 py-2 space-y-1.5">
                    {LIVRAISON_ITEMS.map(item => {
                      const value = checklistData[item.key] || '';
                      const note = checklistNotes[item.key] || '';
                      if (!value) return null;
                      const isDone = value === 'Bon' || value === 'Fait';
                      return (
                        <div key={item.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isDone ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isDone ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          <span className="font-medium flex-1">{item.label}</span>
                          <span className="text-xs font-semibold">{value}</span>
                          {note && <span className="text-[10px] italic opacity-75 ml-1">({note})</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Finalize button */}
            {isAtelierReady && !isFinalized && (
              <Button onClick={handleFinalize} disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="finalize-livraison-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Fermer la fiche - Vehicule pret a etre livre
              </Button>
            )}

            {/* Finalized */}
            {isFinalized && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center" data-testid="livraison-finalized-banner">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                <p className="text-sm font-semibold text-emerald-700">Vehicule pret a etre livre</p>
                <p className="text-xs text-emerald-600">La preparation livraison est terminee</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
              {hasWo && (
                <Button variant="outline" className="text-sm text-blue-600"
                  onClick={() => { onClose(false); navigate(`/admin/work-orders/${prep.work_order_id}`); }}
                  data-testid="livraison-view-wo-btn">
                  <Wrench className="w-4 h-4 mr-1" /> Voir la fiche de travail
                </Button>
              )}
              {!isFinalized && (
                <Button variant="outline" className="text-sm text-red-500 hover:bg-red-50 ml-auto" onClick={handleDelete}
                  disabled={saving} data-testid="livraison-delete-btn">
                  Supprimer
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ======================================
// Main export - routes to correct dialog based on type
// ======================================
const PreparationChecklist = ({ vehicleId, vehicleName, type, open, onClose, onUpdate }) => {
  if (type === 'parc') {
    return <ParcOccasionDialog vehicleId={vehicleId} vehicleName={vehicleName} open={open} onClose={onClose} onUpdate={onUpdate} />;
  }
  return <LivraisonDialog vehicleId={vehicleId} vehicleName={vehicleName} open={open} onClose={onClose} onUpdate={onUpdate} />;
};

export default PreparationChecklist;
